import { useState, useEffect } from 'react';

// ── Form steps definition ──────────────────────────────────────────────────
const STEPS = [
    {
        id: 'idea',
        title: 'Questions about your idea',
        fields: [
            {
                id: 'idea',
                label: 'What is your idea?',
                hint: 'Your idea remains yours. This application is covered by a non-disclosure agreement.',
                type: 'textarea'
            },
            {
                id: 'founder',
                label: 'How did you come up with the idea?',
                hint: 'Tell me about the circumstances and your thought process.',
                type: 'textarea'
            },
            {
                id: 'icp',
                label: 'Do you personally know at least one person who needs this?',
                hint: 'It could be yourself, it could be someone from your social circle.',
                type: 'textarea'
            },
            {
                id: 'solution',
                label: 'How will your idea improve their life?',
                hint: 'Tell me about the direct effect of using your idea and the ripple effect of having improved that specific area of their life.',
                type: 'textarea'
            },
            {
                id: 'market',
                label: 'Are there existing products that address same or similar issues?',
                hint: 'Those existing products are missing the point. Tell me what they miss.',
                type: 'textarea'
            },
            {
                id: 'plan',
                label: 'What is your best case scenario for a 5-year plan for this?',
                hint: 'Tell me about the dream; we\'ll fix reality to match.',
                type: 'textarea'
            }
        ],
    },
    {
        id: 'contact',
        title: 'Your contact info',
        fields: [
            {
                id: 'name',
                label: 'Your name',
                type: 'text',
                placeholder: 'First name is fine',
            },
            {
                id: 'email',
                label: 'Your email',
                type: 'email',
                placeholder: "We'll send your confirmation here",
            },
        ],
    },
];

const STORAGE_KEY = 'nursery_draft';

function saveLocal(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (_) { }
}

function loadLocal() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
}

// ── Sub-components ────────────────────────────────────────────────────────

function FormField({ field, value, onChange }) {
    const common = {
        value: value || '',
        onChange: (e) => onChange(field.id, e.target.value),
        placeholder: field.placeholder || '',
        id: field.id,
    };

    return (
        <div className="form-field">
            <label htmlFor={field.id}>{field.label}</label>
            {field.hint && <div className="field-hint">{field.hint}</div>}
            {field.type === 'textarea' ? (
                <textarea {...common} rows={4} />
            ) : field.type === 'select' ? (
                <select {...common}>
                    <option value="">Select…</option>
                    {field.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            ) : (
                <input {...common} type={field.type} />
            )}
        </div>
    );
}

function StepProgress({ current, total }) {
    return (
        <div className="form-progress">
            {Array.from({ length: total }).map((_, i) => (
                <div
                    key={i}
                    className={`step-dot ${i < current ? 'done' : i === current ? 'active' : ''}`}
                />
            ))}
        </div>
    );
}

function ReviewScreen({ answers, onEdit, onPay, paying }) {
    const allFields = STEPS.flatMap((s) => s.fields);
    const labelMap = Object.fromEntries(allFields.map((f) => [f.id, f.label]));

    return (
        <div>
            <h2>Your submission</h2>
            <p style={{ marginBottom: '2.5rem' }}>
                Read through your answers. When you're happy, submit and pay below — your idea review
                will be waiting for Ela on the other side.
            </p>

            {Object.entries(answers).map(([key, val]) =>
                val ? (
                    <div className="review-card" key={key}>
                        <div className="review-label">{labelMap[key] || key}</div>
                        <div className="review-value">{val}</div>
                    </div>
                ) : null
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button className="btn-secondary" onClick={onEdit}>Edit answers</button>
            </div>

            <div className='payment-box-container'>
                <div className="payment-box">
                    <div className="payment-amount">€42</div>
                    <div className="payment-desc">
                        One-time idea review fee. Ela will read your submission and respond within 5 business days
                        with honest, detailed written feedback.
                    </div>
                    <button
                        className="btn-primary"
                        onClick={onPay}
                        disabled={paying}
                        style={{ opacity: paying ? 0.6 : 1 }}
                    >
                        {paying ? 'Redirecting to payment…' : 'Submit & pay →'}
                    </button>
                </div>

                <div className='payment-box'>
                    You retain full ownership of your idea at all times. 
                    <br /><br />
                    Everything you share is kept confidential and never disclosed without your permission.
                    <br /><br />
                    Your idea will be used only to evaluate it as part of this paid service.
                    <br /><br />
                    Any further use or development will only happen with your agreement.
                </div>
            </div>
        </div>
    );
}

function SuccessScreen() {
    return (
        <div style={{ maxWidth: 560, paddingTop: '2rem' }}>
            <h2>You're in.</h2>
            <p>
                Your idea has been submitted. Ela will review it and send you written feedback within
                5 business days. Check your inbox for a confirmation email.
            </p>
            <p>
                In the meantime — trust that submitting it was already a brave step.
            </p>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────

export default function SubmitIdeaPage() {
    const [view, setView] = useState('form'); // 'form' | 'review' | 'success'
    const [stepIndex, setStepIndex] = useState(0);
    const [answers, setAnswers] = useState(loadLocal);
    const [paying, setPaying] = useState(false);

    // Persist draft on every change
    useEffect(() => { saveLocal(answers); }, [answers]);

    // Check for Stripe success redirect
    useEffect(() => {
        setView('info');
        if (window.location.search.includes('payment=success')) {
            setView('success');
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const setField = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));

    const currentStep = STEPS[stepIndex];

    const handleNext = () => {
        if (stepIndex < STEPS.length - 1) {
            setStepIndex((i) => i + 1);
        } else {
            setView('review');
        }
    };

    const handleBack = () => {
        if (stepIndex > 0) setStepIndex((i) => i - 1);
    };

    const handlePay = async () => {
        setPaying(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/submissions`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(answers),
                }
            );
            const { checkoutUrl } = await res.json();
            window.location.href = checkoutUrl;
        } catch (err) {
            console.error(err);
            alert('Something went wrong. Please try again or email nursery@startup-doula.com');
            setPaying(false);
        }
    };

    if (view === 'success') {
        return (
            <div className="page-container">
                <div className="container">
                    <SuccessScreen />
                </div>
            </div>
        );
    }

    if (view === 'review') {
        return (
            <div className="page-container">
                <div className="container">
                    <ReviewScreen
                        answers={answers}
                        onEdit={() => { setStepIndex(0); setView('form'); }}
                        onPay={handlePay}
                        paying={paying}
                    />
                </div>
            </div>
        );
    }

    // Form view
    return (
        <div className="page-container">
            <div className="container">
                <div className="row">
                    <div className="col-md-8">

                        <StepProgress current={stepIndex} total={STEPS.length} />

                        <h2 style={{ marginBottom: '2.5rem' }}>{currentStep.title}</h2>

                        <div className="nursery-form">
                            {currentStep.fields.map((field) => (
                                <FormField
                                    key={field.id}
                                    field={field}
                                    value={answers[field.id]}
                                    onChange={setField}
                                />
                            ))}
                        </div>

                        <div className="btn-row">
                            {stepIndex > 0 && (
                                <button className="btn-secondary" onClick={handleBack}>← Back</button>
                            )}
                            <button className="btn-primary" onClick={handleNext}>
                                {stepIndex < STEPS.length - 1 ? 'Next →' : 'Review my answers →'}
                            </button>
                        </div>

                        <p style={{ marginTop: '1.5rem', fontSize: 'clamp(8pt,0.8vw,10pt)', color: '#888' }}>
                            Your answers are saved automatically. You can close this page and come back.
                        </p>

                    </div>
                </div>
            </div>
        </div>
    );
}